import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

// 3D Phone Carousel Component
const PhoneCarousel3D = ({ images }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const autoPlayRef = useRef(null);
  const touchStartX = useRef(0);

  // Preload all images on mount
  useEffect(() => {
    let loadedCount = 0;
    const totalImages = images.length;
    
    images.forEach((image) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
        }
      };
      img.src = image.src;
    });
  }, [images]);

  // Auto-play
  useEffect(() => {
    if (!imagesLoaded) return;
    
    autoPlayRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % images.length);
    }, 4000);
    
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [imagesLoaded, images.length]);

  const pauseAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  };

  const resumeAutoPlay = () => {
    setTimeout(() => {
      if (!autoPlayRef.current) {
        autoPlayRef.current = setInterval(() => {
          setActiveIndex(prev => (prev + 1) % images.length);
        }, 4000);
      }
    }, 5000);
  };

  const goToSlide = (index) => {
    pauseAutoPlay();
    let newIndex = index;
    if (index < 0) newIndex = images.length - 1;
    else if (index >= images.length) newIndex = 0;
    setActiveIndex(newIndex);
    resumeAutoPlay();
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    pauseAutoPlay();
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setActiveIndex(prev => (prev + 1) % images.length);
      } else {
        setActiveIndex(prev => (prev - 1 + images.length) % images.length);
      }
    }
    resumeAutoPlay();
  };

  // Calculate position for each phone
  const getPosition = (index) => {
    const total = images.length;
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;
    return diff;
  };

  if (!imagesLoaded) {
    return (
      <div className="relative h-[400px] sm:h-[450px] md:h-[500px] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="relative w-full select-none">
      <div
        className="relative h-[400px] sm:h-[450px] md:h-[500px] mx-auto overflow-visible"
        style={{ perspective: '1000px' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((image, index) => {
          const position = getPosition(index);
          const isCenter = position === 0;
          const isLeft = position === -1;
          const isRight = position === 1;
          const isVisible = Math.abs(position) <= 1;

          if (!isVisible) return null;

          return (
            <div
              key={image.src}
              className="absolute top-1/2 transition-all duration-500 ease-out"
              style={{
                left: isCenter ? '50%' : isLeft ? '18%' : '82%',
                transform: `translateX(-50%) translateY(-50%) rotateY(${isCenter ? 0 : isLeft ? 35 : -35}deg) scale(${isCenter ? 1 : 0.75})`,
                zIndex: isCenter ? 30 : 20,
                opacity: isCenter ? 1 : 0.7,
              }}
            >
              <div className="w-48 sm:w-56 md:w-64">
                <div className="bg-gray-900 rounded-[2rem] sm:rounded-[2.5rem] p-2 sm:p-3 shadow-2xl">
                  <div className="bg-black rounded-[1.5rem] sm:rounded-[2rem] p-0.5 sm:p-1 relative overflow-hidden">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 sm:w-20 h-4 sm:h-5 bg-black rounded-b-xl z-10"></div>
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full rounded-[1.25rem] sm:rounded-[1.75rem] object-cover"
                      draggable={false}
                      loading="eager"
                    />
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-[2rem] sm:rounded-[2.5rem] pointer-events-none"></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={() => goToSlide(activeIndex - 1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-40 p-2 sm:p-3 bg-gray-900/20 hover:bg-gray-900/40 active:bg-gray-900/50 backdrop-blur-sm rounded-full text-gray-800"
        aria-label="Previous slide"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={() => goToSlide(activeIndex + 1)}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-40 p-2 sm:p-3 bg-gray-900/20 hover:bg-gray-900/40 active:bg-gray-900/50 backdrop-blur-sm rounded-full text-gray-800"
        aria-label="Next slide"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 mt-4">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              index === activeIndex 
                ? 'bg-purple-600 w-8' 
                : 'bg-gray-400 hover:bg-gray-600 w-2.5'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-1/2 h-8 bg-black/20 blur-2xl rounded-full pointer-events-none"></div>
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          // Parse the end value (handles strings like "1,000" or "99.9")
          const numericEnd = parseFloat(end.toString().replace(/,/g, ''));
          const startTime = Date.now();
          
          const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            // Easing function for smooth deceleration
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = numericEnd * easeOutQuart;
            
            if (numericEnd >= 100) {
              setCount(Math.floor(currentValue));
            } else {
              setCount(parseFloat(currentValue.toFixed(1)));
            }
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(numericEnd);
            }
          };
          
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  // Format number with commas
  const formatNumber = (num) => {
    if (decimals > 0) {
      return num.toFixed(decimals);
    }
    if (num >= 1000) {
      return num.toLocaleString();
    }
    return num;
  };

  return (
    <span ref={ref}>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
};

// Cookie Consent Banner Component
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gray-900 border-t border-gray-700 shadow-2xl transform transition-transform duration-500 ease-out"
      style={{ animation: 'slideUp 0.5s ease-out' }}
      data-testid="cookie-consent-banner"
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-start sm:items-center gap-3 text-gray-300 text-sm">
          <svg className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5 sm:mt-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>
            We use cookies to enhance your experience and analyse site traffic. By clicking "Accept", you consent to our use of cookies. 
            <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline ml-1">
              Learn more
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
            data-testid="cookie-decline-btn"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="px-5 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all"
            data-testid="cookie-accept-btn"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [isSupportDropdownOpen, setIsSupportDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(true);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [appStoreUrls, setAppStoreUrls] = useState({ ios: null, android: null });

  // Fetch subscription packages for pricing section
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscription-packages`);
        if (Array.isArray(response.data)) {
          // Transform backend data to pricing plan format
          const plans = response.data
            .filter(pkg => pkg.active !== false)
            .sort((a, b) => {
              // Sort by price ascending
              const priceA = parseFloat((a.price || '0').replace(/[^0-9.]/g, ''));
              const priceB = parseFloat((b.price || '0').replace(/[^0-9.]/g, ''));
              return priceA - priceB;
            })
            .map(pkg => {
              const features = pkg.features || {};
              
              // All features shown on every tier — enabled or not
              const allFeatures = [
                { label: `${features.maxSites === 'Unlimited' ? 'Unlimited' : 'Up to ' + (features.maxSites || '0')} sites`, enabled: true },
                { label: `${features.maxTeamMembers === 'Unlimited' ? 'Unlimited' : 'Up to ' + (features.maxTeamMembers || '0')} team members`, enabled: true },
                { label: `${features.storage || '0'} GB storage`, enabled: true },
                { label: 'Digital work diary', enabled: !!features.workDiary },
                { label: 'Task management', enabled: !!features.taskManagement },
                { label: 'Photo gallery', enabled: !!features.photoGallery },
                { label: 'Materials', enabled: !!features.procurement },
                { label: 'Mobile App (Team Members)', enabled: true },
                { label: 'Mobile App (Admins)', enabled: !!features.adminMobileApp },
                { label: 'Push Notifications (Mobile App)', enabled: !!features.pushNotifications },
                { label: 'Quotes & Invoices', enabled: !!features.quotesAndInvoices },
                { label: 'Payments & CIS Statements', enabled: !!features.payments },
                { label: 'Team expenses', enabled: !!features.expenses },
                { label: 'Team overtime', enabled: !!features.overtime },
                { label: 'Team holidays', enabled: !!features.holidaysModule },
                { label: 'Offline mode (Team Members)', enabled: !!features.offlineMode },
                { label: 'Progress reports', enabled: !!features.progressReports },
                { label: 'Neo AI Assistant', enabled: !!features.neo },
                { label: 'AI Report Generation', enabled: !!features.aiReportGeneration },
              ];
              
              return {
                name: pkg.name,
                price: pkg.price || '£0',
                period: '/month',
                description: pkg.tier_key === 'starter' 
                  ? 'Perfect for small teams getting started'
                  : pkg.tier_key === 'business'
                  ? 'For growing construction businesses'
                  : 'For enterprises with complex needs',
                allFeatures,
                popular: pkg.tier_key === 'business', // Business is marked as popular
                cta: 'Get Started'
              };
            });
          
          setPricingPlans(plans);
        }
      } catch (err) {
        console.error('Error fetching pricing plans:', err);
        // Fallback to empty
        setPricingPlans([]);
      } finally {
        setPricingLoading(false);
      }
    };
    
    fetchPricingPlans();
  }, []);

  // Fetch featured FAQs from backend
  useEffect(() => {
    const fetchFeaturedFaqs = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/faqs/featured`);
        if (response.data.success) {
          setFaqs(response.data.faqs || []);
        }
      } catch (err) {
        console.error('Error fetching featured FAQs:', err);
        // Fallback to empty - section will be hidden
        setFaqs([]);
      } finally {
        setFaqsLoading(false);
      }
    };
    
    fetchFeaturedFaqs();
  }, []);

  // Fetch app store URLs from backend
  useEffect(() => {
    const fetchAppStoreUrls = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/app/metadata`);
        setAppStoreUrls({
          ios: response.data.update_url_ios || null,
          android: response.data.update_url_android || null
        });
      } catch (err) {
        console.error('Error fetching app store URLs:', err);
      }
    };
    
    fetchAppStoreUrls();
  }, []);

  // Handle scroll for navbar background and active section
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Determine active section
      const sections = ['home', 'features', 'mobile-app', 'pricing', 'about'];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'features', label: 'Features' },
    { id: 'mobile-app', label: 'Mobile App' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'about', label: 'About' },
    { id: 'support', label: 'Support', isDropdown: true },
  ];

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Time & Attendance',
      description: 'GPS-enabled clock-in/out with geofencing. Track your workforce in real-time with automated timesheets.',
      color: 'blue'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: 'Digital Work Diary',
      description: 'Replace paper logs with digital entries. Photo documentation, weather tracking, and daily progress reports.',
      color: 'purple'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      title: 'Task Management',
      description: 'Create, assign, and track tasks across all your sites. Set priorities, deadlines, and monitor completion.',
      color: 'green'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'Expense Tracking',
      description: 'Submit expenses with photo receipts. Streamlined approval workflows and automatic categorisation.',
      color: 'orange'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: 'Real-Time Reports',
      description: 'Instant dashboards with actionable insights. Export reports for stakeholders and compliance.',
      color: 'pink'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'Team Management',
      description: 'Manage roles, permissions, and capacity. Track performance and optimise resource allocation.',
      color: 'indigo'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Quoting & Invoicing',
      description: 'Create professional quotes, convert to invoices in one click, and generate branded PDFs. Built-in templates and CIS compliance.',
      color: 'blue'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Supplier Discovery',
      description: 'Find nearby suppliers like Screwfix, Toolstation, and Travis Perkins mapped to each of your sites. Source materials faster.',
      color: 'green'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: 'AI Site Assistant',
      description: 'Ask Neo anything about your sites, teams, or tasks. Get instant answers, generate reports, and automate routine operations with AI.',
      color: 'purple'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Holiday Management',
      description: 'Handle leave requests, approvals, and team availability in one place. See who is off and plan around absences.',
      color: 'orange'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: 'Site Management',
      description: 'Add and manage all your sites with interactive maps, geofencing boundaries, capacity tracking, and department assignments.',
      color: 'indigo'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v2m0 8v2" />
        </svg>
      ),
      title: 'Overtime Tracking',
      description: 'Log, review, and approve overtime hours with full visibility. Keep labour costs under control across every site.',
      color: 'pink'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Photo Documentation',
      description: 'Capture site progress with timestamped photos. Browse, filter, and share visual records across your entire project portfolio.',
      color: 'green'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
        </svg>
      ),
      title: 'Procurement',
      description: 'Raise purchase orders, track deliveries, and manage supplier spending. Keep every project on budget with full audit trails.',
      color: 'orange'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      title: 'Billing & Subscriptions',
      description: 'Flexible subscription plans that scale with your business. Manage payments, upgrade tiers, and track billing history in one place.',
      color: 'blue'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'CIS Compliance',
      description: 'Generate CIS statements and tax documentation automatically. Stay compliant with HMRC requirements without the paperwork.',
      color: 'indigo'
    },
  ];

  const testimonials = [
    {
      quote: "SiteOrbit has transformed how we manage our construction sites. The real-time tracking and digital work diary have saved us countless hours of paperwork.",
      author: "James Mitchell",
      role: "Operations Director",
      company: "BuildRight Construction",
      image: "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop"
    },
    {
      quote: "The mobile app is brilliant. Our team can clock in, submit expenses, and update tasks all from their phones. It's made site management so much easier.",
      author: "Sarah Thompson",
      role: "Project Manager",
      company: "Meridian Developments",
      image: "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop"
    },
    {
      quote: "We've reduced our admin time by 60% since implementing SiteOrbit. The automated reports and Neo AI assistant are game-changers.",
      author: "David Chen",
      role: "Managing Director",
      company: "Apex Construction Group",
      image: "https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop"
    },
  ];

  const stats = [
    { value: 10, suffix: '+', label: 'Tools in 1 App' },
    { value: 0, suffix: '', label: 'iOS, Android & Web', isText: true, displayText: 'Mobile + Web' },
    { value: 0, suffix: '', label: 'Unlimited Sites & Team Members*', isText: true, displayText: 'Unlimited' },
    { value: 5.0, suffix: '\u2605', label: 'App Rating', decimals: 1 },
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-gray-900'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <div className="flex-shrink-0 cursor-pointer" onClick={() => scrollToSection('home')}>
              <h1 className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">Site</span>
                <span className={isScrolled ? 'text-gray-900' : 'text-white'}>Orbit</span>
              </h1>
            </div>

            {/* Desktop Navigation - Center */}
            {!isMobile && (
              <div className="flex flex-1 items-center justify-center space-x-6 lg:space-x-8">
                {navLinks.map((link) => (
                  link.isDropdown ? (
                    <div key={link.id} className="relative">
                      <button
                        onClick={() => setIsSupportDropdownOpen(!isSupportDropdownOpen)}
                        onBlur={() => setTimeout(() => setIsSupportDropdownOpen(false), 150)}
                        className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center ${
                          isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white hover:text-blue-300'
                        }`}
                        data-testid="support-dropdown-btn"
                      >
                        {link.label}
                        <svg className={`w-4 h-4 ml-1 transition-transform ${isSupportDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isSupportDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
                          <button
                            onClick={() => navigate('/faqs')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            data-testid="faqs-link"
                          >
                            <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            FAQs
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      key={link.id}
                      onClick={() => scrollToSection(link.id)}
                      className={`text-sm font-medium transition-colors whitespace-nowrap ${
                        activeSection === link.id 
                          ? isScrolled ? 'text-blue-600' : 'text-blue-400'
                          : isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white hover:text-blue-300'
                      }`}
                    >
                      {link.label}
                    </button>
                  )
                ))}
              </div>
            )}

            {/* Desktop CTA Buttons */}
            {!isMobile && (
              <div className="flex items-center space-x-4">
                {/* Login Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                    onBlur={() => setTimeout(() => setIsLoginDropdownOpen(false), 150)}
                    className={`text-sm font-medium transition-colors flex items-center ${
                      isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white hover:text-blue-300'
                    }`}
                  >
                    Login
                    <svg className={`w-4 h-4 ml-1 transition-transform ${isLoginDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isLoginDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
                      <button
                        onClick={() => navigate('/team/login')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        data-testid="team-portal-nav-btn"
                      >
                        <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Team Portal
                      </button>
                      <button
                        onClick={() => navigate('/admin/login')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Admin Portal
                      </button>
                      {/* Super Admin menu item - only visible in development */}
                      {process.env.NODE_ENV === 'development' && (
                        <button
                          onClick={() => navigate('/super-admin/login')}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Super Admin
                        </button>
                      )}
                    </div>
                  )}
              </div>
              <button
                onClick={() => navigate('/register')}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-full hover:shadow-lg hover:scale-105 transition-all"
              >
                Get Started Free
              </button>
            </div>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-lg ${isScrolled ? 'text-gray-600' : 'text-white'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobile && isMenuOpen && (
          <div className="bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-2">
              {navLinks.filter(link => !link.isDropdown).map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-medium ${
                    activeSection === link.id ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </button>
              ))}
              {/* Support Section in Mobile */}
              <div className="pt-2 pb-1">
                <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Support</p>
              </div>
              <button
                onClick={() => { navigate('/faqs'); setIsMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                data-testid="mobile-faqs-link"
              >
                FAQs
              </button>
              <hr className="my-2" />
              <button
                onClick={() => navigate('/team/login')}
                className="block w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                data-testid="mobile-team-portal-btn"
              >
                Team Portal
              </button>
              <button
                onClick={() => navigate('/admin/login')}
                className="block w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Admin Login
              </button>
              {/* Super Admin menu item - only visible in development */}
              {process.env.NODE_ENV === 'development' && (
                <button
                  onClick={() => navigate('/super-admin/login')}
                  className="block w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Super Admin Login
                </button>
              )}
              <button
                onClick={() => navigate('/register')}
                className="block w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg text-center"
              >
                Get Started Free
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/2138126/pexels-photo-2138126.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Construction site"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
              Trusted by construction teams across the UK
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Construction Site Management,{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">
                Simplified
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Streamline operations, track progress in real-time, and manage your workforce with 
              our all-in-one construction management platform. From time tracking to task management, 
              SiteOrbit keeps your projects on orbit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center"
              >
                Get Started Free
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/tour')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center"
                data-testid="landing-see-how-it-works-btn"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                See How It Works
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 text-white/60 text-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                No credit card required
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                GDPR Compliant
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                UK-based support
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
                  {stat.isText ? (
                    <span>{stat.displayText}</span>
                  ) : (
                    <AnimatedCounter 
                      end={stat.value} 
                      suffix={stat.suffix} 
                      duration={2000}
                      decimals={stat.decimals || 0}
                    />
                  )}
                </div>
                <div className="text-blue-100 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Your Sites
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful tools designed specifically for construction professionals. 
              Simplify your workflow and keep every project on track.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110 ${
                  feature.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                  feature.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                  feature.color === 'green' ? 'bg-green-100 text-green-600' :
                  feature.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                  feature.color === 'pink' ? 'bg-pink-100 text-pink-600' :
                  'bg-indigo-100 text-indigo-600'
                }`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Features Image */}
          <div className="mt-20 relative">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="text-white">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4">Real-Time Site Visibility</h3>
                  <p className="text-blue-100 mb-6 leading-relaxed">
                    Get instant visibility into every aspect of your construction sites. 
                    Monitor worker attendance, track task progress, and review daily reports 
                    all from one centralised dashboard.
                  </p>
                  <ul className="space-y-3">
                    {['Live attendance tracking', 'Instant progress updates', 'Photo documentation', 'Custom alerts'].map((item, i) => (
                      <li key={i} className="flex items-center text-blue-100">
                        <svg className="w-5 h-5 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative">
                  <img
                    src="https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt="Construction team collaboration"
                    className="rounded-2xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section id="mobile-app" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-1 lg:order-1">
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                📱 Available on iOS & Android
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Manage Your Sites From Anywhere
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our mobile app puts the full power of SiteOrbit in your pocket. 
                Perfect for site managers on the go and field workers who need quick access 
                to tasks, time tracking, and daily logs.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  { icon: '📍', title: 'GPS Clock-In/Out', desc: 'Geofenced attendance with automatic site detection' },
                  { icon: '📸', title: 'Photo Documentation', desc: 'Capture and attach photos directly to work entries' },
                  { icon: '📶', title: 'Offline Mode', desc: 'Works without internet - syncs when connected' },
                  { icon: '🔔', title: 'Push Notifications', desc: 'Instant alerts for tasks, approvals, and updates' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start">
                    <span className="text-2xl mr-4">{item.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* App Store Badges */}
              <div className="flex flex-wrap gap-4">
                <a 
                  href={appStoreUrls.ios || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`inline-block transition-transform hover:scale-105 ${!appStoreUrls.ios ? 'opacity-50 pointer-events-none' : ''}`}
                  data-testid="app-store-link"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                    alt="Download on App Store"
                    className="h-12"
                  />
                </a>
                <a 
                  href={appStoreUrls.android || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`inline-block transition-transform hover:scale-105 ${!appStoreUrls.android ? 'opacity-50 pointer-events-none' : ''}`}
                  data-testid="play-store-link"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    className="h-12"
                  />
                </a>
              </div>
            </div>

            <div className="order-2 lg:order-2 relative">
              <PhoneCarousel3D 
                images={[
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/c6vma8qs_Admin%20App_01.jpg",
                    alt: "SiteOrbit Admin Dashboard - Key metrics, clock-in status, tasks overview and weather"
                  },
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/afk4729l_Admin%20App_02.jpg",
                    alt: "SiteOrbit Tasks - Weekly task view with filters and progress tracking"
                  },
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/bug43n88_Admin%20App_03.jpg",
                    alt: "SiteOrbit Work Diary - Daily attendance records with photos and hours"
                  },
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/hif5jk6p_Team%20App_01.jpg",
                    alt: "SiteOrbit Team App - Confirm Clock In with site and location details"
                  },
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/3lbnuctg_Team%20App_02.jpg",
                    alt: "SiteOrbit Team Dashboard - Clocked in state with site info and photos"
                  },
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/5fy4v5g7_Team%20App_03.jpg",
                    alt: "SiteOrbit Team Tasks - Weekly tasks with status updates"
                  },
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/ux9kjj26_Team%20App_04.jpg",
                    alt: "SiteOrbit Work Diary - Clock records with progress photos"
                  },
                  { 
                    src: "https://customer-assets.emergentagent.com/job_paypal-fix-5/artifacts/pdt756yh_Team%20App_05.jpg",
                    alt: "SiteOrbit Request Materials - Order supplies form"
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Start with a 14-day free trial. No credit card required. 
              Choose the plan that fits your business.
            </p>
          </div>

          {pricingLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : pricingPlans.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {pricingPlans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl p-8 ${
                    plan.popular 
                      ? 'ring-2 ring-blue-600 shadow-xl' 
                      : 'border border-gray-200 shadow-sm'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                    <div className="flex flex-col items-center justify-center">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                        <span className="text-gray-500 ml-1">{plan.period}</span>
                      </div>
                      <span className="text-xs text-gray-400 mt-1">(inc. VAT)</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.allFeatures.map((item, i) => (
                      <li key={i} className={`flex items-center text-sm ${item.enabled ? 'text-gray-600' : 'text-gray-400'}`}>
                        {item.enabled ? (
                          <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 mr-3 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                        {item.label}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/register')}
                    className={`w-full py-3 rounded-xl font-medium transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Get Started
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Unable to load pricing plans. Please try again later.
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Construction Professionals
            </h2>
            <p className="text-lg text-gray-600">
              See what our customers have to say about SiteOrbit
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 lg:py-32 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Built by Construction Professionals, for Construction Professionals
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                SiteOrbit was founded in 2020 by a team of construction industry professionals who 
                saw first-hand the challenges of managing multiple sites with outdated tools and 
                paper-based systems.
              </p>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Based in London, UK, our mission is to give construction businesses of all sizes 
                access to enterprise-grade management tools without the enterprise price tag. 
                We're passionate about helping teams work smarter, not harder.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 rounded-xl p-6">
                  <div className="text-3xl font-bold text-blue-400 mb-1">2020</div>
                  <div className="text-gray-400 text-sm">Founded</div>
                </div>
                <div className="bg-white/10 rounded-xl p-6">
                  <div className="text-3xl font-bold text-purple-400 mb-1">5.0★</div>
                  <div className="text-gray-400 text-sm">App Rating</div>
                </div>
                <div className="bg-white/10 rounded-xl p-6">
                  <div className="text-3xl font-bold text-green-400 mb-1">UK</div>
                  <div className="text-gray-400 text-sm">Headquarters</div>
                </div>
                <div className="bg-white/10 rounded-xl p-6">
                  <div className="text-3xl font-bold text-pink-400 mb-1">24/7</div>
                  <div className="text-gray-400 text-sm">Support Available</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1238864/pexels-photo-1238864.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Construction site overview"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Only show if there are featured FAQs */}
      {!faqsLoading && faqs.length > 0 && (
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600">
                Got questions? We've got answers.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id || index}
                  className="border border-gray-200 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-600 whitespace-pre-line">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Link to full FAQ page */}
            <div className="text-center mt-8">
              <Link 
                to="/faqs" 
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                View all FAQs
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Site Management?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join 1,000+ construction companies already using SiteOrbit
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Start Your Free Trial
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="flex flex-col items-center text-center space-y-8">
            {/* Brand */}
            <div>
              <h3 className="text-2xl font-bold mb-2">
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">Site</span>Orbit
              </h3>
              <p className="text-gray-400 text-sm">
                Construction site management made simple.
              </p>
            </div>

            {/* App Store Badges */}
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={appStoreUrls.ios || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`inline-block transition-transform hover:scale-105 ${!appStoreUrls.ios ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid="footer-app-store-link"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                  alt="Download on App Store"
                  className="h-10"
                />
              </a>
              <a 
                href={appStoreUrls.android || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`inline-block transition-transform hover:scale-105 ${!appStoreUrls.android ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid="footer-play-store-link"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  className="h-10"
                />
              </a>
            </div>

            {/* Social Links */}
            <div className="flex space-x-6">
              {/* Facebook */}
              <a href="https://www.facebook.com/profile.php?id=61587580947923" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              {/* Twitter/X */}
              <a href="https://x.com/siteorbitapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              {/* Instagram */}
              <a href="https://www.instagram.com/siteorbitapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-10 pt-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
              <p>© {new Date().getFullYear()} SiteOrbit Ltd. All rights reserved.</p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsentBanner />
    </div>
  );
};

export default LandingPage;
